import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HOUSE_STATUSES,
  LIMITS,
  MEMBER_STATUSES,
  OWNERSHIPS,
  RATE_PLANS,
  RELATIONS,
  RESIDENCES,
  formatSequence,
  type HouseDetail,
  type HouseStatus,
  type Member,
  type MemberInput,
  type MemberStatus,
  type Ownership,
  type RatePlan,
  type Relation,
  type Residence,
} from '@mahallu/shared';
import { ApiError, REGISTER_KEYS, register, useSequences, useWards } from '../api';
import { FormFooter, FormSection, Modal, NumberField, SelectField, TextField } from '../components/Form';

/** Until the rate plans master exists (slice 7), a new plan suggests its usual monthly amount. */
const DEFAULT_RATE: Record<RatePlan, number> = { STANDARD: 100, NRI: 250, EXEMPT: 0 };
const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const blankToNull = (v: string) => (v.trim() === '' ? null : v.trim());

function useRefreshRegister() {
  const qc = useQueryClient();
  return () => Promise.all(REGISTER_KEYS.map((k) => qc.invalidateQueries({ queryKey: [...k] })));
}

interface PersonState {
  nameEn: string;
  nameMl: string;
  gender: 'M' | 'F' | '';
  birthYear: number | '';
  residence: Residence;
  country: string;
  region: string;
  city: string;
  work: string;
  phone: string;
}

const emptyPerson: PersonState = { nameEn: '', nameMl: '', gender: '', birthYear: '', residence: 'RESIDENT', country: '', region: '', city: '', work: '', phone: '' };

function personFrom(m: Member): PersonState {
  return {
    nameEn: m.nameEn,
    nameMl: m.nameMl ?? '',
    gender: m.gender ?? '',
    birthYear: m.birthYear ?? '',
    residence: m.residence,
    country: m.country ?? '',
    region: m.region ?? '',
    city: m.city ?? '',
    work: m.work ?? '',
    phone: m.phone ?? '',
  };
}

function personBody(p: PersonState): Omit<MemberInput, 'relation'> {
  const away = p.residence !== 'RESIDENT';
  return {
    nameEn: p.nameEn.trim(),
    nameMl: blankToNull(p.nameMl),
    gender: p.gender || null,
    birthYear: p.birthYear === '' ? null : p.birthYear,
    residence: p.residence,
    country: away ? blankToNull(p.country) : null,
    region: away ? blankToNull(p.region) : null,
    city: away ? blankToNull(p.city) : null,
    work: blankToNull(p.work),
    phone: blankToNull(p.phone),
  };
}

/** Person fields shared by "add house" (the head) and "add / edit member". */
function PersonFields({ p, set }: { p: PersonState; set: (next: Partial<PersonState>) => void }) {
  const { t } = useTranslation();
  const away = p.residence !== 'RESIDENT';
  return (
    <>
      <TextField label={t('field.personEn')} value={p.nameEn} onChange={(v) => set({ nameEn: v })} maxLength={LIMITS.name} required />
      <TextField label={t('field.personMl')} value={p.nameMl} onChange={(v) => set({ nameMl: v })} maxLength={LIMITS.name} lang="ml" />
      <SelectField
        label={t('field.gender')}
        value={p.gender}
        empty={t('common.notGiven')}
        options={[
          { value: 'M', label: t('gender.M') },
          { value: 'F', label: t('gender.F') },
        ]}
        onChange={(v) => set({ gender: v })}
      />
      <NumberField label={t('field.birthYear')} value={p.birthYear} min={1900} max={new Date().getFullYear()} onChange={(v) => set({ birthYear: v })} />
      <TextField label={t('field.phone')} value={p.phone} onChange={(v) => set({ phone: v })} inputMode="tel" maxLength={30} hint={t('form.phoneHint')} />
      <SelectField label={t('field.lives')} value={p.residence} required options={RESIDENCES.map((r) => ({ value: r, label: t(`res.${r}`) }))} onChange={(v) => set({ residence: (v || 'RESIDENT') as Residence })} />
      {away ? (
        <>
          <TextField label={p.residence === 'ABROAD' ? t('field.countryOnly') : t('field.stateOnly')} value={p.country} onChange={(v) => set({ country: v })} maxLength={LIMITS.place} />
          <TextField label={t('field.city')} value={p.city} onChange={(v) => set({ city: v })} maxLength={LIMITS.place} />
          {p.residence === 'ABROAD' ? <TextField label={t('field.regionAbroad')} value={p.region} onChange={(v) => set({ region: v })} maxLength={LIMITS.place} /> : null}
        </>
      ) : null}
      <TextField label={t('field.work')} value={p.work} onChange={(v) => set({ work: v })} maxLength={LIMITS.work} />
    </>
  );
}

export function HouseCreateForm({ onClose, onCreated }: { onClose: () => void; onCreated: (h: HouseDetail) => void }) {
  const { t, i18n } = useTranslation();
  const ml = i18n.language === 'ml';
  const wards = useWards();
  const seqs = useSequences();
  const refresh = useRefreshRegister();
  const [wardCode, setWardCode] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameMl, setNameMl] = useState('');
  const [ownership, setOwnership] = useState<Ownership | ''>('');
  const [plan, setPlan] = useState<RatePlan>('STANDARD');
  const [rate, setRate] = useState<number | ''>(DEFAULT_RATE.STANDARD);
  const [head, setHead] = useState<PersonState>(emptyPerson);
  const save = useMutation({
    mutationFn: () =>
      register.createHouse({ wardCode, nameEn: nameEn.trim(), nameMl: blankToNull(nameMl), ownership: ownership || null, ratePlan: plan, monthlyRateInr: rate === '' ? 0 : rate, head: personBody(head) }),
    onSuccess: async (h) => {
      await refresh();
      onCreated(h);
    },
  });
  const houseSeq = seqs.data?.find((s) => s.code === 'HOUSE');
  const next = houseSeq?.next.find((n) => n.scopeKey === wardCode)?.nextValue ?? 1;
  const preview = wardCode && houseSeq ? formatSequence(houseSeq.pattern, next, { at: new Date(), ward: wardCode }) : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Modal title={t('house.add')} onClose={onClose} wide footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('house.add')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form id="active-form" onSubmit={submit}>
        <FormSection title={t('house.section')}>
          <SelectField
            label={t('field.ward')}
            value={wardCode}
            required
            empty={t('form.choose')}
            options={(wards.data ?? []).map((w) => ({ value: w.code, label: ml && w.nameMl ? w.nameMl : w.nameEn }))}
            onChange={(v) => setWardCode(v)}
            hint={preview ? t('house.numberPreview', { no: preview }) : undefined}
          />
          <SelectField label={t('field.ownership')} value={ownership} empty={t('common.notGiven')} options={OWNERSHIPS.map((o) => ({ value: o, label: t(`ownership.${o}`) }))} onChange={(v) => setOwnership(v)} />
          <TextField label={t('field.nameEn')} value={nameEn} onChange={setNameEn} maxLength={LIMITS.name} required />
          <TextField label={t('field.nameMl')} value={nameMl} onChange={setNameMl} maxLength={LIMITS.name} lang="ml" />
          <SelectField
            label={t('field.plan')}
            value={plan}
            required
            options={RATE_PLANS.map((p) => ({ value: p, label: t(`plan.${p}`) }))}
            onChange={(v) => {
              const p = (v || 'STANDARD') as RatePlan;
              setPlan(p);
              setRate(DEFAULT_RATE[p]);
            }}
          />
          <NumberField label={t('field.monthlyRate')} value={rate} min={0} max={100000} required onChange={setRate} />
        </FormSection>
        <FormSection title={t('house.headSection')}>
          <PersonFields p={head} set={(n) => setHead((h) => ({ ...h, ...n }))} />
        </FormSection>
        <p className="note">{t('house.consentNote')}</p>
      </form>
    </Modal>
  );
}

export function HouseEditForm({ house, onClose }: { house: HouseDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshRegister();
  const [nameEn, setNameEn] = useState(house.nameEn);
  const [nameMl, setNameMl] = useState(house.nameMl ?? '');
  const [ownership, setOwnership] = useState<Ownership | ''>(house.ownership ?? '');
  const [plan, setPlan] = useState<RatePlan>(house.ratePlan);
  const [rate, setRate] = useState<number | ''>(house.monthlyRateInr);
  const [status, setStatus] = useState<HouseStatus>(house.status);
  const save = useMutation({
    mutationFn: () =>
      register.updateHouse(house.id, { version: house.version, nameEn: nameEn.trim(), nameMl: blankToNull(nameMl), ownership: ownership || null, ratePlan: plan, monthlyRateInr: rate === '' ? 0 : rate, status }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Modal title={`${t('house.edit')} · ${house.houseNo}`} onClose={onClose} footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('form.save')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('house.section')}>
          <TextField label={t('field.nameEn')} value={nameEn} onChange={setNameEn} maxLength={LIMITS.name} required />
          <TextField label={t('field.nameMl')} value={nameMl} onChange={setNameMl} maxLength={LIMITS.name} lang="ml" />
          <SelectField label={t('field.ownership')} value={ownership} empty={t('common.notGiven')} options={OWNERSHIPS.map((o) => ({ value: o, label: t(`ownership.${o}`) }))} onChange={setOwnership} />
          <SelectField label={t('field.plan')} value={plan} required options={RATE_PLANS.map((p) => ({ value: p, label: t(`plan.${p}`) }))} onChange={(v) => setPlan((v || 'STANDARD') as RatePlan)} />
          <NumberField label={t('field.monthlyRate')} value={rate} min={0} max={100000} required onChange={setRate} />
          <SelectField
            label={t('field.status')}
            value={status}
            required
            options={HOUSE_STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))}
            onChange={(v) => setStatus((v || 'ACTIVE') as HouseStatus)}
            hint={t('house.statusHint')}
          />
        </FormSection>
      </form>
    </Modal>
  );
}

export function MemberForm({ houseId, member, onClose }: { houseId: string; member?: Member; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshRegister();
  const [p, setP] = useState<PersonState>(member ? personFrom(member) : emptyPerson);
  const [relation, setRelation] = useState<Relation | ''>(member?.relation ?? '');
  const [status, setStatus] = useState<MemberStatus>(member?.status ?? 'ALIVE');
  const save = useMutation({
    mutationFn: () => {
      const body = { ...personBody(p), relation: (relation || 'OTHER') as Relation };
      return member ? register.updateMember(member.id, { ...body, status, version: member.version }) : register.addMember(houseId, body);
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Modal
      title={member ? t('member.edit') : t('member.add')}
      onClose={onClose}
      wide
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={member ? t('form.save') : t('member.add')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('member.section')}>
          <SelectField label={t('field.relation')} value={relation} required empty={t('form.choose')} options={RELATIONS.map((r) => ({ value: r, label: t(`rel.${r}`) }))} onChange={setRelation} />
          {member ? (
            <SelectField
              label={t('field.status')}
              value={status}
              required
              options={MEMBER_STATUSES.map((s) => ({ value: s, label: t(`mstatus.${s}`) }))}
              onChange={(v) => setStatus((v || 'ALIVE') as MemberStatus)}
              hint={t('member.statusHint')}
            />
          ) : null}
          <PersonFields p={p} set={(n) => setP((x) => ({ ...x, ...n }))} />
        </FormSection>
      </form>
    </Modal>
  );
}
